import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Switch,
  Divider,
  FormControlLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  useTSQUser,
  useTSMCreateUser,
  useTSMUpdateUser,
} from "../../hooks/useTSUsers";
import { useTSQPeopleList } from "../../hooks/useTSEntities";
import { EmployeeFull } from "../../types/personTypes";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import {
  UserCreate,
  UserUpdate,
  UserRole,
  UserRoles,
} from "../../types/userTypes";

// Validation schema
const userSchema = yup.object({
  id: yup.string(),
  first_name: yup.string().required("YUP: First Name is required"),
  last_name: yup.string().required("YUP: Last Name is required"),
  email: yup
    .string()
    .matches(/^[^\s@]+@centese\.com$/, "Email must have a centese domain.")
    .required("YUP: Email is required"),
  role: yup.string().oneOf(UserRoles).required("YUP: Role is required"),
  password: yup.string().when("id", {
    is: (id: string) => !!id,
    then: (schema) =>
      schema
        .required("Password is required")
        .min(8, "YUP: Password must be at least 8 characters.")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}/,
          "Password must be at least 8 characters long and contain at least one of each of the following: Uppercase Letter (A-Z), Lowercase Letter (a-z), Number (0-9), Special Character (@$!%*?&)"
        ),
    otherwise: (schema) => schema.nullable(),
  }),
  confirmPassword: yup.string().when(["id", "password"], {
    is: (id: string, password: string) => !!id || password,
    then: (schema) =>
      schema
        .oneOf([yup.ref("password")], "Passwords must match")
        .required("Please confirm password"),
  }),
  is_superuser: yup.bool().nullable(),
});

export const PageUserForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Mode detection from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const formMode = searchParams.get("mode") || "create";
  const isViewMode = formMode === "view";
  const isEditMode = Boolean(id) && formMode === "edit";
  const isCreateMode = !id && formMode === "create";
  const { user } = useAuth();
  const {
    data: userData,
    isLoading: userLoading,
    error: userError,
  } = useTSQUser(id || "");

  const {
    data: employeeResponse,
    isLoading: empsLoading,
    error: empsError,
  } = useTSQPeopleList(
    {
      skip: -1,
      limit: -1,
      active_status: "active",
      filters: [[]],
    },
    "employee"
  );

  const isLoading = userLoading || empsLoading;

  const createUserMutation = useTSMCreateUser();
  const updateUserMutation = useTSMUpdateUser();

  const isSaving = createUserMutation.isPending || updateUserMutation.isPending;

  // State
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<EmployeeFull[]>([]);
  const [updatePassword, setUpdatePassword] = useState<boolean>(false);

  interface UserFormData {
    first_name: string;
    last_name: string;
    email: string;
    password?: string;
    confirmPassword?: string;
    role: string;
    is_record_active: boolean;
    is_superuser: boolean;
    is_verified: boolean;
    employee_id?: string;
  }

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<UserFormData>({
    resolver: yupResolver(userSchema) as any,
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: undefined,
      confirmPassword: undefined,
      role: "",
      is_record_active: true,
      is_superuser: false,
      is_verified: false,
      employee_id: undefined,
    },
  });

  const selectedEmployeeId = watch("employee_id");
  const isSuperUser = watch("is_superuser");

  // Mode switching functions
  const switchToViewMode = () => {
    navigate(`/users/form?mode=view&id=${id}`);
  };

  const switchToEditMode = () => {
    navigate(`/users/form?mode=edit&id=${id}`);
  };

  const switchToCreateMode = () => {
    navigate(`/users/form?mode=create`);
  };

  useEffect(() => {
    if (employeeResponse?.items) {
      setEmployees(employeeResponse?.items as EmployeeFull[]);
    }
  }, [employeeResponse]);

  useEffect(() => {
    if (userData && (isEditMode || isViewMode)) {
      reset({
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        role: userData.role,
        is_record_active: userData.is_record_active,
        is_superuser: userData.is_superuser,
        is_verified: userData.is_verified,
        employee_id: userData.employee?.id,
      });
    }
  }, [userData, isEditMode, isViewMode, reset]);

  // Form submission
  const onSubmit = async (data: UserFormData) => {
    try {
      setError(null);

      if (isCreateMode) {
        const userPayload = {
          first_name: data.first_name,
          last_name: data.last_name,
          role: data.role as UserRole,
          email: data.email,
          password: data.password!,
          is_superuser: data.is_superuser,
          employee_id: data.employee_id!,
        };
        const new_user = await createUserMutation.mutateAsync(userPayload);
        navigate(`/users/form?mode=view&id=${new_user.id}`);
      } else if (isEditMode) {
        if (!id) {
          throw new Error("User ID is required for updates");
        }
        const userPayload = {
          id: id,
          first_name: data.first_name,
          last_name: data.last_name,
          role: data.role as UserRole,
          email: data.email,
          password: data.password,
          is_superuser: data.is_superuser,
          employee_id: data.employee_id,
        };
        await updateUserMutation.mutateAsync({
          id: id,
          data: userPayload,
        });
        navigate(`/users/form?mode=view&id=${id}`);
      }
    } catch (err: any) {
      console.error("Failed to save user:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to save user"
      );
    }
  };

  const handleCancel = () => {
    navigate("/users");
  };

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          variant="outlined"
        >
          Back to Users
        </Button>
        <Typography variant="h4" component="h1">
          {isCreateMode
            ? "Create User"
            : isViewMode
            ? `View User: ${
                userData?.first_name.concat(` ${userData?.last_name}`) || ""
              }`
            : `Edit User: ${
                userData?.first_name.concat(` ${userData?.last_name}`) || ""
              }`}
        </Typography>
        {isViewMode && (
          <Button
            variant="outlined"
            onClick={switchToCreateMode}
            startIcon={<AddIcon />}
          >
            Create New
          </Button>
        )}
        {isViewMode && (
          <Button
            variant="outlined"
            onClick={switchToEditMode}
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        )}
        {isEditMode && (
          <Button
            variant="outlined"
            onClick={switchToViewMode}
            startIcon={<VisibilityIcon />}
          >
            View
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={() => {
            console.log(getValues(), userData);
          }}
        >
          DEBUG PRINT
        </Button>
      </Stack>

      {/* Error Alert */}
      {(error || userError || empsError) && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error ||
            userError?.message ||
            empsError?.message ||
            "An unknown error occurred"}
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit, console.error)}>
          <Stack spacing={3}>
            {/* Basic Information */}
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Stack direction="column" gap={2}>
              <Box display="flex" gap={2}>
                <Controller
                  name="first_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="First Name"
                      fullWidth
                      disabled={isViewMode}
                      error={!!errors.first_name}
                      helperText={errors.first_name?.message || ""}
                    />
                  )}
                />
                <Controller
                  name="last_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Last Name"
                      fullWidth
                      disabled={isViewMode}
                      error={!!errors.last_name}
                      helperText={errors.last_name?.message || ""}
                    />
                  )}
                />
              </Box>
              <Box display="flex" gap={2}>
                <Controller
                  name="employee_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      disabled={isViewMode}
                      error={!!errors.employee_id}
                    >
                      <InputLabel>Link Employee</InputLabel>
                      <Select {...field} label="Link Employee">
                        {employees.map((emp) => (
                          <MenuItem key={emp.id} value={emp.id}>
                            {emp.first_name} {emp.last_name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.employee_id && (
                        <FormHelperText>
                          {errors.employee_id.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Stack direction="column" gap={2}>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        disabled={isViewMode || isSuperUser}
                        error={!!errors.role}
                      >
                        <InputLabel>User Role</InputLabel>
                        <Select {...field} label="User Role">
                          <MenuItem key="admin" value="admin">
                            {"Admin"}
                          </MenuItem>
                          <MenuItem key="manager" value="manager">
                            {"Manager"}
                          </MenuItem>
                          <MenuItem key="user" value="user">
                            {"User"}
                          </MenuItem>
                        </Select>
                        {errors.role && (
                          <FormHelperText>{errors.role.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="is_superuser"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e)}
                            disabled={
                              isViewMode ||
                              userData?.id === user?.user_id ||
                              !user?.is_superuser
                            }
                          />
                        }
                        label="SuperUser"
                      />
                    )}
                  />
                </Stack>
              </Box>
              <Box display="flex" gap={2}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      type="email"
                      autoComplete="off"
                      fullWidth
                      disabled={isViewMode}
                      error={!!errors.email}
                      helperText={errors.email?.message || ""}
                    />
                  )}
                />
                <Stack direction="column" gap={2} minWidth={300}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Password"
                        type="password"
                        autoComplete="off"
                        fullWidth
                        disabled={isViewMode || (isEditMode && !updatePassword)}
                        error={!!errors.password}
                        helperText={errors.password?.message || ""}
                      />
                    )}
                  />
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Confirm Password"
                        type="password"
                        autoComplete="off"
                        fullWidth
                        disabled={isViewMode || (isEditMode && !updatePassword)}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message || ""}
                      />
                    )}
                  />
                  {isEditMode && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={updatePassword}
                          onChange={() => {
                            setUpdatePassword(!updatePassword);
                          }}
                        />
                      }
                      label="Update Password"
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* Form Actions */}
            {!isViewMode && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  pt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Saving..."
                    : isCreateMode
                    ? "Create User"
                    : "Update User"}
                </Button>
              </Box>
            )}
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};
